import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Users, Settings, Heart, ArrowRight, CheckCircle, Star,
  Building2, Shield, Zap, TrendingUp, Sparkles, Award,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import { trackCTAClick, trackViewContent } from "../utils/analytics";
import { useEffect, useRef, useState } from "react";
import ReferralPartnerWidget from "../components/ReferralPartnerWidget";

const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const Reveal = ({ children, className = "", delay = 0 }) => {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
      style={{ transitionDuration: "800ms", transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const WaveDivider = ({ fill = "#F7F7F7" }) => (
  <div className="relative -mb-1">
    <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block">
      <path d="M0 100V40C180 70 360 10 540 30C720 50 900 90 1080 70C1260 50 1350 20 1440 40V100H0Z" fill={fill} />
    </svg>
  </div>
);

const Home = () => {
  useEffect(() => { trackViewContent("Homepage", "Landing Page"); }, []);

  const stats = [
    { value: "150+", label: "Organizations Supported", icon: Building2 },
    { value: "15+", label: "Years of Healthcare Experience", icon: Award },
    { value: "98%", label: "Client Satisfaction", icon: Star },
    { value: "500+", label: "Projects & Initiatives Delivered", icon: Zap },
  ];

  const impactAreas = [
    {
      icon: Users,
      title: "Build a Stronger Team",
      tagline: "Your people are at the heart of everything you do.",
      description: "We help healthcare organizations attract, develop, and retain great talent while creating stronger leaders, more engaged employees, and healthier workplace cultures.",
      services: ["Leadership Development", "Staff Training & Development", "Employee Engagement", "Performance Management", "Talent Acquisition Support", "Organizational Culture"],
      path: "/services/workforce-development",
    },
    {
      icon: Settings,
      title: "Improve Daily Operations",
      tagline: "Efficient operations create better experiences for both patients and staff.",
      description: "From streamlining workflows to implementing new processes and technology, we will help your organization operate more effectively and confidently.",
      services: ["Workflow Optimization", "Process Improvement", "Project Management", "Administrative Support", "SOP Development", "Technology Implementation"],
      path: "/services/clinic-operations",
    },
    {
      icon: Heart,
      title: "Deliver a Better Patient Experience",
      tagline: "Exceptional patient care starts long before the provider enters the room.",
      description: "We help organizations improve the systems, communication, and service standards that shape every patient interaction.",
      services: ["Front Desk Training", "Patient Experience Programs", "Scheduling & Communication Improvements", "Service Standards", "Patient Journey Optimization"],
      path: "/services/patient-experience",
    },
    {
      icon: Shield,
      title: "Stay Compliant & Prepared",
      tagline: "Healthcare regulations are always changing.",
      description: "We help you stay ahead with practical guidance that keeps your organization organized, compliant, and ready for what is next.",
      services: ["Policy & Procedure Development", "Audit & Accreditation Readiness", "Licensing Support", "Regulatory Compliance", "Documentation Reviews", "Quality Improvement"],
      path: "/services/compliance-credentialing",
    },
    {
      icon: TrendingUp,
      title: "Grow with Confidence",
      tagline: "Whether you are launching a new practice, expanding your services, or leading a major organizational initiative.",
      description: "We will help you build a strong foundation for sustainable growth.",
      services: ["Healthcare Startup Support", "Strategic Planning", "Practice Expansion", "Change Management", "Market Development", "Fractional Leadership Support"],
      path: "/services/growth-scalability",
    },
  ];

  const programs = [
    { name: "Foundations First", description: "Build efficient workflows, strengthen internal processes, and create the operational foundation for long-term success.", path: "/programs/foundations-first", icon: Settings },
    { name: "CareRevive", description: "Create a healthier workplace culture through leadership development, employee engagement, and strategies that improve retention and reduce burnout.", path: "/programs/carerevive", icon: Heart },
    { name: "Concierge Frontline Experience", description: "Empower your front office team with the tools and training needed to deliver a consistently exceptional patient experience.", path: "/programs/concierge-frontline", icon: Users },
  ];

  const testimonials = [
    { quote: "RNB quickly became an extension of our leadership team. Their ability to understand our organization and provide practical, actionable solutions made an immediate impact.", author: "Healthcare Organization Leader", role: "Medical Director" },
    { quote: "Working with RNB gave us confidence during a period of rapid growth. They helped us strengthen our processes, improve communication, and better support both our staff and patients.", author: "Practice Administrator", role: "Operations Director" },
  ];

  return (
    <div className="overflow-hidden" style={{ fontFamily: "'DM Sans', 'Public Sans', system-ui, sans-serif" }}>
      <SEO
        title="More Than Consultants | Your Healthcare Growth Partner"
        description="RNB Consulting Group helps healthcare organizations strengthen their teams, improve operations, navigate change, and build the foundation for long-term success."
        keywords="healthcare consulting, healthcare operations, healthcare growth partner, medical practice consulting, healthcare workforce development"
      />
      <StructuredData data={[
        { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" }] },
        { "@context": "https://schema.org", "@type": "LocalBusiness", "@id": "https://www.rnbconsultinggroup.com/#business", "name": "RNB Consulting Group", "description": "Your Healthcare Growth Partner. We help healthcare organizations strengthen their teams, improve operations, and build the foundation for long-term success.", "url": "https://www.rnbconsultinggroup.com", "telephone": "(888) 250-4605", "email": "info@rnbconsultinggroup.com", "address": { "@type": "PostalAddress", "addressLocality": "Atlanta", "addressRegion": "GA", "addressCountry": "US" }, "priceRange": "$$" },
        { "@context": "https://schema.org", "@type": "ProfessionalService", "name": "RNB Consulting Group", "url": "https://www.rnbconsultinggroup.com", "telephone": "(888) 250-4605", "areaServed": [{ "@type": "Country", "name": "United States" }] },
      ]} />

      {/* ═══ HERO ═══ */}
      <section className="relative min-h-[85vh] sm:min-h-[90vh] flex items-center overflow-hidden bg-[#0B1F3A]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1653566031535-bcf33e1c2893?w=1920&h=1080&fit=crop&q=80"
            alt="Healthcare partnership"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A]/95 via-[#0B1F3A]/85 to-[#00838A]/30" />
        </div>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-[10%] w-64 sm:w-80 h-64 sm:h-80 bg-[#C9A84C]/10 rounded-full blur-[100px] animate-float" />
          <div className="absolute bottom-32 left-[5%] w-72 sm:w-96 h-72 sm:h-96 bg-[#00838A]/15 rounded-full blur-[120px] animate-float" style={{ animationDelay: "3s" }} />
        </div>

        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-20 pb-28 relative z-10 w-full text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 mb-6 sm:mb-8">
              <Sparkles className="w-4 h-4 text-[#C9A84C]" />
              <span className="text-xs sm:text-sm font-medium text-white/90">Your Healthcare Growth Partner</span>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight text-white mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="hero-headline"
            >
              More Than Consultants.{" "}
              <span className="block sm:inline text-[#C9A84C]">Your Healthcare Growth Partner.</span>
            </h1>
          </Reveal>

          <Reveal delay={200}>
            <p className="text-sm sm:text-base lg:text-lg text-white/75 leading-relaxed mb-4 max-w-2xl mx-auto">
              Running a healthcare organization takes more than great clinical care. It takes the right people, efficient operations, and a trusted partner who understands the challenges behind the scenes.
            </p>
          </Reveal>

          <Reveal delay={250}>
            <p className="text-sm sm:text-base text-white/60 leading-relaxed mb-8 sm:mb-10 max-w-2xl mx-auto">
              At RNB Consulting Group, we help healthcare organizations strengthen their teams, improve operations, navigate change, and build the foundation for long-term success.
            </p>
          </Reveal>

          <Reveal delay={350}>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <div className="sm:w-auto">
                <Link to="/consultation" className="block">
                  <Button
                    size="lg"
                    className="w-full bg-[#00838A] hover:bg-[#00838A]/90 text-white rounded-full px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-semibold shadow-lg shadow-[#00838A]/25 hover:-translate-y-0.5 transition-all duration-300"
                    data-testid="hero-cta-primary"
                    onClick={() => trackCTAClick("Schedule a Consultation", "Homepage Hero")}
                  >
                    Schedule a Consultation <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                </Link>
              </div>
              <div className="sm:w-auto">
                <Link to="/services" className="block">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full border-2 border-white/25 text-white hover:bg-white/10 rounded-full px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-semibold transition-all duration-300"
                    data-testid="hero-cta-secondary"
                    onClick={() => trackCTAClick("Explore Our Services", "Homepage Hero")}
                  >
                    Explore Our Services
                  </Button>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-10">
          <WaveDivider fill="#F7F7F7" />
        </div>
      </section>

      {/* Referral Widget (only visible to referral partners) */}
      <section className="py-4 bg-[#F7F7F7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <ReferralPartnerWidget />
        </div>
      </section>

      {/* ═══ TRUSTED BY ═══ */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[#F7F7F7]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-center text-xs sm:text-sm font-semibold uppercase tracking-wider text-[#00838A] mb-8 sm:mb-10">
              Trusted by Healthcare Organizations
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 lg:gap-6">
            {stats.map((stat, idx) => (
              <Reveal key={stat.label} delay={idx * 100}>
                <div
                  className="text-center p-4 sm:p-6 bg-white rounded-xl sm:rounded-2xl shadow-[0_2px_12px_rgba(11,31,58,0.04)] hover:shadow-[0_8px_24px_rgba(0,131,138,0.08)] hover:-translate-y-1 transition-all duration-500"
                  data-testid={`stat-card-${idx}`}
                >
                  <stat.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#C9A84C] mx-auto mb-2 sm:mb-3" />
                  <div
                    className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#0B1F3A] mb-1"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-[11px] sm:text-xs lg:text-sm text-[#5A5A5A] leading-tight">{stat.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ INTRO ═══ */}
      <section className="py-14 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3A] mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="intro-heading"
            >
              Helping Healthcare Organizations Solve Today{'\u2019'}s Challenges and Prepare for Tomorrow
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-sm sm:text-base lg:text-lg text-[#5A5A5A] leading-relaxed mb-5">
              Healthcare is constantly evolving. Whether you are focused on improving day-to-day operations, supporting your team, enhancing the patient experience, or planning for growth, it can be difficult to know where to start.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-base sm:text-lg font-semibold text-[#00838A] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              That{'\u2019'}s where we come in.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-sm sm:text-base text-[#5A5A5A] leading-relaxed">
              We partner with healthcare organizations to identify opportunities, solve complex challenges, and implement practical solutions that create lasting results. Every engagement is tailored to your goals, your team, and your organization because no two healthcare organizations are exactly alike.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══ WHERE WE MAKE THE BIGGEST IMPACT ═══ */}
      <section className="py-14 sm:py-20 lg:py-24 bg-[#F7F7F7]" data-testid="impact-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-5 sm:mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E8F4F5] text-[#00838A] text-xs sm:text-sm font-semibold mb-4 sm:mb-5">
                <Sparkles className="w-4 h-4" /> What We Do
              </span>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3A] mb-3 sm:mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Where We Make the Biggest Impact
              </h2>
            </div>
          </Reveal>
          <Reveal delay={50}>
            <p className="text-center text-sm sm:text-base lg:text-lg text-[#5A5A5A] max-w-2xl mx-auto mb-10 sm:mb-14 leading-relaxed">
              Every organization has different priorities. Whether you are solving a specific challenge or looking for ongoing strategic support, our team is here to help where it matters most.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-7">
            {impactAreas.map((area, idx) => (
              <Reveal key={area.title} delay={idx * 80}>
                <Card
                  className={`group border-0 bg-white shadow-[0_4px_20px_rgba(11,31,58,0.05)] hover:shadow-[0_12px_40px_rgba(0,131,138,0.1)] rounded-2xl transition-all duration-500 hover:-translate-y-1 h-full ${idx >= 3 ? "lg:max-w-[calc(100%)] md:last:col-span-1" : ""}`}
                  data-testid={`impact-card-${idx}`}
                >
                  <CardContent className="p-5 sm:p-6 lg:p-7 flex flex-col h-full">
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[#E8F4F5] flex items-center justify-center mb-4 group-hover:bg-[#00838A]/15 transition-colors duration-300">
                      <area.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#00838A]" />
                    </div>
                    <h3
                      className="text-lg sm:text-xl font-bold text-[#0B1F3A] mb-2"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {area.title}
                    </h3>
                    <p className="text-xs sm:text-sm font-medium text-[#00838A] mb-3">{area.tagline}</p>
                    <p className="text-xs sm:text-sm text-[#5A5A5A] leading-relaxed mb-4 sm:mb-5">{area.description}</p>
                    <div className="mt-auto">
                      <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-[#0B1F3A]/40 mb-2 sm:mb-3">
                        Popular services include:
                      </p>
                      <div className="space-y-1.5 sm:space-y-2">
                        {area.services.map((service) => (
                          <div key={service} className="flex items-start gap-2">
                            <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#00838A] shrink-0 mt-0.5" />
                            <span className="text-xs sm:text-sm text-[#5A5A5A]">{service}</span>
                          </div>
                        ))}
                      </div>
                      <Link
                        to={area.path}
                        className="inline-flex items-center gap-2 mt-4 sm:mt-5 text-[#00838A] font-semibold text-sm hover:gap-3 transition-all duration-300"
                      >
                        Learn More <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ WHY ORGANIZATIONS PARTNER WITH RNB ═══ */}
      <section className="py-14 sm:py-20 lg:py-24 bg-[#0B1F3A] relative overflow-hidden" data-testid="why-partner-section">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-[15%] w-64 sm:w-80 h-64 sm:h-80 bg-[#00838A]/12 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-[10%] w-56 sm:w-72 h-56 sm:h-72 bg-[#C9A84C]/8 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10 text-center">
          <Reveal>
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-5 sm:mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Why Organizations Partner With RNB
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-sm sm:text-base lg:text-lg text-white/70 leading-relaxed mb-5">
              Healthcare organizations need more than recommendations. They need a partner who understands their industry and is invested in their success.
            </p>
          </Reveal>
          <Reveal delay={150}>
            <p className="text-sm sm:text-base text-white/60 leading-relaxed mb-5">
              Our team works alongside yours to provide strategic guidance, hands-on implementation, and ongoing support tailored to your unique goals.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-sm sm:text-base text-white/60 leading-relaxed mb-6 sm:mb-8">
              Whether we are helping improve operations, train staff, lead projects, strengthen leadership, or prepare for growth, our focus is always the same:
            </p>
          </Reveal>
          <Reveal delay={250}>
            <p
              className="text-base sm:text-lg lg:text-xl font-bold text-[#C9A84C] leading-relaxed"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Helping your organization operate more efficiently so you can focus on delivering exceptional care.
            </p>
          </Reveal>
        </div>
      </section>

      <WaveDivider fill="#E8F4F5" />

      {/* ═══ FEATURED PROGRAMS ═══ */}
      <section className="pt-14 sm:pt-20 pb-16 sm:pb-24 bg-[#E8F4F5]" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-5 sm:mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00838A]/10 text-[#00838A] text-xs sm:text-sm font-semibold mb-4 sm:mb-5">
                <Award className="w-4 h-4" /> Signature Programs
              </span>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3A] mb-3 sm:mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Featured Programs
              </h2>
            </div>
          </Reveal>
          <Reveal delay={50}>
            <p className="text-center text-sm sm:text-base lg:text-lg text-[#5A5A5A] max-w-2xl mx-auto mb-10 sm:mb-14 leading-relaxed">
              Looking for a structured solution? Our signature programs are designed to address some of the most common opportunities we see across healthcare organizations.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 lg:gap-7">
            {programs.map((program, idx) => (
              <Reveal key={program.name} delay={idx * 120}>
                <Card
                  className="group border-0 bg-white shadow-[0_4px_24px_rgba(11,31,58,0.06)] hover:shadow-[0_12px_40px_rgba(0,131,138,0.12)] rounded-2xl transition-all duration-500 hover:-translate-y-2 h-full"
                  data-testid={`program-card-${idx}`}
                >
                  <CardContent className="p-5 sm:p-7 lg:p-8 flex flex-col h-full">
                    <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[#E8F4F5] flex items-center justify-center mb-4 sm:mb-5 group-hover:bg-[#00838A]/15 transition-colors duration-300">
                      <program.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#00838A]" />
                    </div>
                    <h3
                      className="text-lg sm:text-xl lg:text-2xl font-bold text-[#0B1F3A] mb-2 sm:mb-3"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {program.name}
                    </h3>
                    <p className="text-xs sm:text-sm lg:text-base text-[#5A5A5A] leading-relaxed mb-5 sm:mb-6 flex-grow">
                      {program.description}
                    </p>
                    <Link
                      to={program.path}
                      className="inline-flex items-center gap-2 text-[#00838A] font-semibold text-sm hover:gap-3 transition-all duration-300"
                    >
                      Learn More <ArrowRight className="w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <WaveDivider fill="#FFFFFF" />

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="pt-14 sm:pt-20 pb-16 sm:pb-24 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-10 sm:mb-14">
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0B1F3A]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                What Our Clients Say
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 lg:gap-8">
            {testimonials.map((t, idx) => (
              <Reveal key={idx} delay={idx * 150}>
                <Card className="border-0 bg-[#F7F7F7] hover:shadow-[0_12px_40px_rgba(0,131,138,0.1)] rounded-2xl transition-all duration-500 hover:-translate-y-1 h-full">
                  <CardContent className="p-5 sm:p-7 lg:p-10 flex flex-col h-full">
                    <div className="flex gap-1 mb-5 sm:mb-6">
                      {[...Array(5)].map((_, i) => (
                        <Star key={`s-${idx}-${i}`} className="w-4 h-4 sm:w-5 sm:h-5 fill-[#C9A84C] text-[#C9A84C]" />
                      ))}
                    </div>
                    <blockquote className="text-sm sm:text-base lg:text-lg text-[#5A5A5A] italic leading-relaxed mb-6 flex-grow">
                      &ldquo;{t.quote}&rdquo;
                    </blockquote>
                    <div>
                      <div className="font-bold text-[#0B1F3A] text-sm sm:text-base">{t.author}</div>
                      <div className="text-xs sm:text-sm text-[#5A5A5A]">{t.role}</div>
                    </div>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-16 sm:py-24 lg:py-32 bg-[#0B1F3A] relative overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-[15%] w-64 sm:w-80 h-64 sm:h-80 bg-[#00838A]/15 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-[10%] w-56 sm:w-72 h-56 sm:h-72 bg-[#C9A84C]/10 rounded-full blur-[100px]" />
        </div>

        <Reveal>
          <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center relative z-10">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-5 sm:mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Let{'\u2019'}s Build a Stronger Organization Together
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-white/70 mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed">
              Whether you are solving today{'\u2019'}s challenges or planning for tomorrow{'\u2019'}s growth, RNB Consulting Group is here to help you move forward with confidence.
            </p>
            <div className="sm:inline-block">
              <Link to="/consultation" className="block">
                <Button
                  size="lg"
                  className="w-full bg-[#C9A84C] text-[#0B1F3A] hover:bg-[#C9A84C]/90 rounded-full px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-bold shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                  data-testid="cta-consultation"
                  onClick={() => trackCTAClick("Schedule Your Consultation", "Homepage CTA")}
                >
                  Schedule Your Consultation <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
